import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, FileText, Loader2, RefreshCw, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  FileDropzone,
  UploadQueue,
  toQueueItems,
  useCreateDocuments,
  useFileUpload,
  useIndexingQueue,
  useLinkDocumentsToProject,
  useMetadataQueue,
  type DocumentCreatePayload,
  type FileRejection,
  type MetadataJobInput,
  type UploadItem,
  type UploadedFile,
} from "@lumanity/ui/ingestion";
import {
  EvidenceFormFields,
  defaultEvidenceFormFields,
  extractedToFormValues,
  resolveFormFields,
  resolveOwnership,
  type EvidenceDocument,
  type EvidenceFormValues,
  type ExtractedMetadata,
} from "@lumanity/ui/evidence";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getEnv } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { fetchDocuments, type DuplicateCheckResult } from "@/hooks/useDocumentMetadata";
import { ACCEPTED_FILE_TYPES, isAllowedDocumentFile } from "@/lib/fileUtils";
import { useRefreshDocuments } from "@/lib/evidence";
import { projectsApi } from "@/services/projectsApi";
import type { ProjectCustomField } from "@/services/projectCustomFieldsApi";

/**
 * Upload → AI metadata extraction → human review → save → index → link to
 * the project. Built on @lumanity/ui (Ingestion → Recipes → 3): the library
 * supplies the picker, upload, job queues, document creation, linking and the
 * evidence metadata fields; this component owns the order of the steps and
 * FRaiME's own behaviour (duplicate check, resuming extractions after a
 * reload, cancel, per-file re-extract).
 */

const DATA_INGESTION_API_URL = getEnv("VITE_DATA_INGESTION_API_URL") || "http://localhost:8001";
const UPLOAD_PATH = "/api/ingestion/upload-files";

type ExtractionStatus = "queued" | "processing" | "done" | "failed";
type SubmitStage = "idle" | "saving" | "indexing" | "linking";

interface UploadRow {
  /** Stable React key and form key. */
  key: string;
  name: string;
  size: number;
  file?: File;
  /** The slugified name the upload stored the blob under — the document's only link to it. */
  uploadedFilename: string;
  downloadUrl: string;
  /** Correlation id the extraction job runs under until the document is saved (then re-keyed). */
  extractionId?: string;
  /** The enqueue itself failed (no job to poll). */
  enqueueFailed?: boolean;
}

interface DocumentUploadFormProps {
  projectId?: string;
  projectModel?: string;
  isAdvancedRag?: boolean;
  customFields?: ProjectCustomField[];
  onComplete?: () => void;
  lockedOwnership?: string;
  onDirtyChange?: (isDirty: boolean) => void;
}

const FORM_FIELDS = defaultEvidenceFormFields();

const absoluteUrl = (url: string) => (url.startsWith("/blob") ? `${DATA_INGESTION_API_URL}${url}` : url);

/** A placeholder record for the evidence fields, which expect a document; nothing is saved from it. */
function stubDocument(row: UploadRow, ownership: string): EvidenceDocument {
  return {
    id: row.key,
    filename: row.uploadedFilename || row.name,
    title: "",
    authors: "",
    description: "",
    tags: [],
    sourceType: "",
    organization: "",
    country: "",
    year: "",
    evidenceType: "",
    dataRetention: null,
    url: "",
    language: "",
    ownership,
    clientId: null,
    createdAt: "",
    updatedAt: "",
  };
}

function blankValues(customFields: ProjectCustomField[], ownership: string): EvidenceFormValues {
  const custom: Record<string, unknown> = {};
  customFields.forEach((cf) => {
    custom[cf.id] = cf.field_type === "multi_select" ? [] : "";
  });
  return {
    title: "",
    authors: "",
    year: "",
    description: "",
    source_type: "",
    organization: "",
    country: "",
    evidence_type: "",
    data_retention: "",
    url: "",
    language: "",
    ownership,
    client_id: "",
    tags: "",
    custom,
    extra: {},
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

function toCreatePayload(row: UploadRow, values: EvidenceFormValues): DocumentCreatePayload {
  return {
    filename: row.uploadedFilename || row.name,
    title: orNull(values.title),
    authors: orNull(values.authors),
    year: orNull(values.year),
    description: orNull(values.description),
    source_type: orNull(values.source_type),
    organization: orNull(values.organization),
    country: orNull(values.country),
    evidence_type: orNull(values.evidence_type),
    data_retention: orNull(values.data_retention),
    url: orNull(values.url),
    language: orNull(values.language),
    ownership: orNull(values.ownership),
    tags: orNull(values.tags),
  };
}

/** Custom-field values worth saving: drop blanks so the project only stores what was filled in. */
function filledCustomValues(custom: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(custom).filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)),
  );
}

let rowCounter = 0;
const newRowKey = () => `row-${Date.now()}-${rowCounter++}`;

// ── One file's metadata form ─────────────────────────────────────────────────

function RowForm({
  row,
  initial,
  customFields,
  ownershipValue,
  locked,
  register,
}: {
  row: UploadRow;
  initial: EvidenceFormValues;
  customFields: ProjectCustomField[];
  ownershipValue: string;
  locked: boolean;
  register: (key: string, form: UseFormReturn<EvidenceFormValues> | null) => void;
}) {
  const form = useForm<EvidenceFormValues>({ defaultValues: initial });
  const ownership = useMemo(
    () => resolveOwnership({ values: ["Public", "Private"], defaultValue: ownershipValue, isLocked: () => locked }),
    [ownershipValue, locked],
  );
  const fields = useMemo(() => resolveFormFields(FORM_FIELDS, ownership), [ownership]);
  const doc = useMemo(() => stubDocument(row, ownershipValue), [row, ownershipValue]);

  useEffect(() => {
    register(row.key, form);
    return () => register(row.key, null);
  }, [register, row.key, form]);

  return (
    <EvidenceFormFields
      fields={fields}
      form={form}
      doc={doc}
      ownership={ownership}
      customFieldDefs={customFields}
      idPrefix={`upload-${row.key}`}
    />
  );
}

// ── The form ─────────────────────────────────────────────────────────────────

const DocumentUploadForm = ({
  projectId: propProjectId,
  projectModel,
  isAdvancedRag = false,
  customFields = [],
  onComplete,
  lockedOwnership,
  onDirtyChange,
}: DocumentUploadFormProps = {}) => {
  const projectId = propProjectId ?? "";
  const defaultOwnership = lockedOwnership ?? "Private";
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();
  const refreshDocuments = useRefreshDocuments();

  const [rows, setRows] = useState<UploadRow[]>([]);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitStage, setSubmitStage] = useState<SubmitStage>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Duplicate dialog: rows already in the project, waiting for a decision.
  const [duplicates, setDuplicates] = useState<Array<{ row: UploadRow; result: DuplicateCheckResult }>>([]);
  const [duplicateChosen, setDuplicateChosen] = useState<Record<string, boolean>>({});

  const forms = useRef(new Map<string, UseFormReturn<EvidenceFormValues>>());
  const appliedExtractions = useRef(new Set<string>());
  // Bumped when a row's form mounts, so a result that arrived first is still applied.
  const [formsVersion, setFormsVersion] = useState(0);
  const registerForm = useCallback((key: string, form: UseFormReturn<EvidenceFormValues> | null) => {
    if (form) forms.current.set(key, form);
    else forms.current.delete(key);
    setFormsVersion((v) => v + 1);
  }, []);

  // The batch response covers every file, in order; handled once per run (see onUploadSettled).
  const onUploadSettled = useRef<(items: UploadItem<UploadedFile[]>[]) => void>();
  const upload = useFileUpload<UploadedFile[]>({
    batch: true,
    path: UPLOAD_PATH,
    onSettled: (items) => onUploadSettled.current?.(items),
  });
  const queue = useMetadataQueue<ExtractedMetadata>();
  const indexing = useIndexingQueue();
  const createDocuments = useCreateDocuments();
  const linkDocuments = useLinkDocumentsToProject();

  useEffect(() => {
    onDirtyChange?.(rows.length > 0);
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  // ── Extraction status, from the queue ──

  const statusOf = useCallback(
    (row: UploadRow): ExtractionStatus | undefined => {
      if (row.enqueueFailed) return "failed";
      if (!row.extractionId) return undefined;
      const info = queue.documentStatuses[row.extractionId];
      if (!info) return "queued";
      if (info.status === "Queued") return "queued";
      if (info.status === "Processing") return "processing";
      return info.status === "Done" ? "done" : "failed";
    },
    [queue.documentStatuses],
  );

  // Fill each form once, when its extraction finishes. The user's edits win for anything the model left empty.
  useEffect(() => {
    rows.forEach((row) => {
      if (!row.extractionId || appliedExtractions.current.has(row.extractionId)) return;
      const info = queue.documentStatuses[row.extractionId];
      const form = forms.current.get(row.key);
      if (info?.status !== "Done" || !info.result || !form) return;
      appliedExtractions.current.add(row.extractionId);
      form.reset(extractedToFormValues(info.result, form.getValues(), customFields));
    });
  }, [queue.documentStatuses, rows, customFields, formsVersion]);

  /** Poll every extraction still running, plus `extra` (startPolling replaces the previous run). */
  const pollRunning = useCallback(
    (current: UploadRow[], extra: string[] = []) => {
      const running = current
        .filter((r) => r.extractionId && !["done", "failed"].includes(statusOf(r) ?? ""))
        .map((r) => r.extractionId as string);
      const ids = [...new Set([...running, ...extra])];
      if (ids.length) queue.startPolling(ids);
    },
    [queue, statusOf],
  );

  const startExtraction = async (toExtract: UploadRow[], all: UploadRow[]) => {
    if (toExtract.length === 0) return;
    const jobs: MetadataJobInput[] = toExtract.map((row) => ({
      document_id: crypto.randomUUID(),
      filename: row.uploadedFilename || row.name,
      original_name: row.name,
      download_url: row.downloadUrl,
      model: projectModel,
    }));
    const idByKey = new Map(toExtract.map((row, i) => [row.key, jobs[i].document_id]));
    try {
      await queue.enqueue(jobs, projectId || undefined, { customFields });
      const next = all.map((r) => (idByKey.has(r.key) ? { ...r, extractionId: idByKey.get(r.key), enqueueFailed: false } : r));
      setRows(next);
      queue.startPolling(jobs.map((j) => j.document_id));
    } catch {
      setRows(all.map((r) => (idByKey.has(r.key) ? { ...r, enqueueFailed: true } : r)));
    }
  };

  // ── Resume extractions already running for this project (e.g. after a reload) ──

  useEffect(() => {
    if (!projectId) return;
    queue
      .fetchPendingJobs(projectId)
      .then((pending) => {
        if (!pending.jobs.length) return;
        const restored: UploadRow[] = pending.jobs.map((job) => ({
          key: newRowKey(),
          name: job.original_name ?? job.filename,
          size: 0,
          uploadedFilename: job.filename,
          downloadUrl: job.download_url ?? "",
          extractionId: job.document_id,
        }));
        setRows(restored);
        setFilesUploaded(true);
        setExpandedKey(restored[0].key);
        queue.startPolling(restored.map((r) => r.extractionId as string));
      })
      .catch(() => {
        /* non-critical */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // ── Step 1: pick ──

  const addFiles = (files: File[]) => {
    setRejections([]);
    const added = files.map((file) => ({ key: newRowKey(), name: file.name, size: file.size, file, uploadedFilename: "", downloadUrl: "" }));
    setRows((prev) => {
      if (prev.length === 0 && added.length) setExpandedKey(added[0].key);
      return [...prev, ...added];
    });
  };

  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));

  // ── Step 2: upload, check duplicates, extract ──

  /** Project-scoped, filename-only — the only thing known before extraction. Best-effort. */
  const findDuplicates = async (uploaded: UploadRow[]): Promise<Array<{ row: UploadRow; result: DuplicateCheckResult }>> => {
    if (!projectId) return [];
    try {
      const ids = new Set(await projectsApi.getDocumentIds(projectId));
      if (ids.size === 0) return [];
      const all = await queryClient.fetchQuery({ queryKey: ["documents"], queryFn: () => fetchDocuments(getAccessToken) });
      const byFilename = new Map(all.filter((d) => ids.has(d.id)).map((d) => [d.filename.toLowerCase(), d] as const));
      return uploaded.flatMap((row, index) => {
        const match = byFilename.get(row.uploadedFilename.toLowerCase());
        return match
          ? [
              {
                row,
                result: {
                  index,
                  is_duplicate: true,
                  matching_document_id: match.id,
                  match_type: "exact" as const,
                  matched_filename: match.filename,
                  matched_title: match.title || null,
                },
              },
            ]
          : [];
      });
    } catch {
      return [];
    }
  };

  const handleUpload = async () => {
    const pending = rows.filter((r) => r.file);
    if (pending.length === 0) return;
    setUploadError(null);
    upload.clear();
    await upload.upload(pending.map((r) => r.file as File));
  };

  onUploadSettled.current = async (items) => {
    const result = items.every((i) => i.status === "success") ? items[0]?.result : undefined;
    upload.clear();
    if (!result) {
      setUploadError(items.find((i) => i.error)?.error ?? "Upload failed or was cancelled");
      return;
    }
    const pending = rows.filter((r) => r.file);
    const uploadedRows = pending.map((row, i) => ({
      ...row,
      uploadedFilename: result[i]?.filename ?? row.name,
      downloadUrl: absoluteUrl(result[i]?.download_url ?? ""),
    }));
    const byKey = new Map(uploadedRows.map((r) => [r.key, r]));
    const next = rows.map((r) => byKey.get(r.key) ?? r);
    setRows(next);
    setFilesUploaded(true);
    setExpandedKey(next[0]?.key ?? null);

    const found = await findDuplicates(uploadedRows);
    if (found.length > 0) {
      setDuplicates(found);
      setDuplicateChosen({});
      return;
    }
    await startExtraction(uploadedRows, next);
  };

  const resolveDuplicates = async (keepChosen: boolean) => {
    const dropped = new Set(duplicates.filter((d) => !(keepChosen && duplicateChosen[d.row.key])).map((d) => d.row.key));
    const remaining = rows.filter((r) => !dropped.has(r.key));
    setDuplicates([]);
    setDuplicateChosen({});
    setRows(remaining);
    if (remaining.length === 0) {
      setFilesUploaded(false);
      return;
    }
    setExpandedKey(remaining[0].key);
    await startExtraction(remaining.filter((r) => !r.extractionId), remaining);
  };

  const retryExtraction = async (row: UploadRow) => {
    const id = row.extractionId ?? crypto.randomUUID();
    appliedExtractions.current.delete(id);
    try {
      await queue.enqueue(
        [{ document_id: id, filename: row.uploadedFilename || row.name, download_url: row.downloadUrl, model: projectModel }],
        projectId || undefined,
        { customFields },
      );
      const next = rows.map((r) => (r.key === row.key ? { ...r, extractionId: id, enqueueFailed: false } : r));
      setRows(next);
      pollRunning(next, [id]);
    } catch {
      setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, enqueueFailed: true } : r)));
    }
  };

  const cancelExtraction = async () => {
    if (projectId) await queue.cancelPending(projectId).catch(() => 0);
    queue.reset();
    appliedExtractions.current.clear();
    setRows([]);
    setFilesUploaded(false);
    setExpandedKey(null);
  };

  // ── Step 3: save, re-key, index, link ──

  const settled = rows.length > 0 && rows.every((r) => ["done", "failed"].includes(statusOf(r) ?? ""));

  const handleSubmit = async () => {
    if (!settled || submitStage !== "idle") return;
    setSubmitError(null);
    const entries = rows.map((row) => ({ row, values: forms.current.get(row.key)?.getValues() ?? blankValues(customFields, defaultOwnership) }));

    // One at a time, so each created record lines up with its row.
    setSubmitStage("saving");
    const payloads = entries.map(({ row, values }) => toCreatePayload(row, values));
    const { created, failed } = await createDocuments(payloads, { concurrency: 1 });
    const failedPayloads = new Set(failed.map((f) => f.payload));
    const saved = entries.filter((_, i) => !failedPayloads.has(payloads[i])).map((entry, i) => ({ ...entry, doc: created[i] }));

    if (saved.length > 0) {
      // Best-effort: the document is saved either way and can be re-extracted.
      await Promise.all(
        saved.map(({ row, doc }) =>
          row.extractionId ? queue.rekey(row.extractionId, doc.id, projectId || undefined).catch(() => undefined) : undefined,
        ),
      );

      setSubmitStage("indexing");
      try {
        await indexing.enqueue(
          saved.map(({ doc }) => ({
            document_id: doc.id,
            filename: doc.filename,
            doc_action: "ingest",
            rag_mode: isAdvancedRag ? "advanced" : "standard",
          })),
          projectId || undefined,
        );
      } catch (err) {
        setSubmitError(`Indexing failed: ${err instanceof Error ? err.message : "unknown error"}`);
        setSubmitStage("idle");
        return;
      }

      if (projectId) {
        setSubmitStage("linking");
        const customFieldValues: Record<string, Record<string, unknown>> = {};
        saved.forEach(({ doc, values }) => {
          const filled = filledCustomValues(values.custom);
          if (Object.keys(filled).length) customFieldValues[doc.id] = filled;
        });
        // Documents are saved and indexed; carry on even if linking fails.
        await linkDocuments(projectId, saved.map(({ doc }) => doc.id), { customFieldValues }).catch(() => undefined);
      }
      refreshDocuments(projectId || undefined);
    }

    setSubmitStage("idle");
    if (failed.length > 0) {
      // Keep only the rows that didn't save, so the user can fix and retry them.
      const savedKeys = new Set(saved.map(({ row }) => row.key));
      setRows((prev) => prev.filter((r) => !savedKeys.has(r.key)));
      setSubmitError(`Couldn't save ${failed.length} document${failed.length === 1 ? "" : "s"}: ${failed.map((f) => `${f.payload.filename} (${f.error})`).join("; ")}`);
      return;
    }
    queue.reset();
    appliedExtractions.current.clear();
    setRows([]);
    setFilesUploaded(false);
    setExpandedKey(null);
    onComplete?.();
  };

  // ── Render ──

  const isExtracting = queue.isPolling;
  const extractedCount = rows.filter((r) => ["done", "failed"].includes(statusOf(r) ?? "")).length;
  const chosenCount = Object.values(duplicateChosen).filter(Boolean).length;
  const submitLabel = {
    idle: isExtracting ? "Extracting metadata..." : "Continue",
    saving: "Saving...",
    indexing: "Indexing...",
    linking: "Linking to project...",
  }[submitStage];

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-foreground">Document Upload</h2>
        <p className="text-sm text-muted-foreground">Upload documents to the project for indexing in the evidence base</p>
      </div>

      <div className="mb-6 rounded-lg border border-brand-amber/30 bg-brand-amber/10 p-3 text-sm">
        <span className="font-medium">💡 Tip:</span> Upload one or more documents. Metadata will be automatically extracted and can then be
        reviewed before saving.
      </div>

      {!filesUploaded && (
        <div className="mb-6">
          <FileDropzone
            multiple
            accept={ACCEPTED_FILE_TYPES}
            validate={(file) =>
              isAllowedDocumentFile(file.name) ? { valid: true } : { valid: false, message: `${file.name}: only PDF, DOCX and PPTX are supported` }
            }
            onFilesAdmitted={addFiles}
            onRejected={setRejections}
            isBusy={upload.isUploading}
            label="Drag & drop your files here"
            hint="PDF, DOCX or PPTX up to 50MB"
            browseLabel="Browse Files"
          />
        </div>
      )}

      {rejections.length > 0 && (
        <p className="mb-4 text-sm text-destructive">
          Skipped {rejections.length} file{rejections.length === 1 ? "" : "s"} — only PDF, DOCX and PPTX are supported:{" "}
          {rejections.map((r) => r.file.name).join(", ")}
        </p>
      )}
      {uploadError && <p className="mb-4 text-sm text-destructive">Upload failed: {uploadError}</p>}
      {submitError && <p className="mb-4 text-sm text-destructive">{submitError}</p>}

      {upload.isUploading && (
        <div className="mb-4">
          <UploadQueue items={toQueueItems(upload.items)} overallProgress={upload.overallProgress} onCancel={upload.cancel} />
        </div>
      )}

      {rows.length > 0 && !upload.isUploading && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {filesUploaded && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Files uploaded successfully. Fill in the metadata below, then save to index and continue.
            </div>
          )}

          {isExtracting && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <span className="flex-1">
                Extracting metadata {extractedCount} of {rows.length}... Fields will auto-populate when done.
              </span>
              {projectId && (
                <button
                  type="button"
                  onClick={() => void cancelExtraction()}
                  className="rounded border border-blue-300 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-100"
                >
                  Cancel
                </button>
              )}
            </div>
          )}

          <div className="mb-6 space-y-3">
            {rows.map((row) => {
              const isExpanded = expandedKey === row.key;
              const status = filesUploaded ? statusOf(row) : undefined;
              const queuePosition = row.extractionId ? queue.documentStatuses[row.extractionId]?.queue_position : undefined;
              return (
                <div key={row.key} className="overflow-hidden rounded-lg border bg-card">
                  <div className="flex items-center justify-between px-4 py-3">
                    <button
                      type="button"
                      className="flex items-center gap-2 truncate text-sm font-medium text-foreground transition-colors hover:text-brand-teal"
                      onClick={() => setExpandedKey(isExpanded ? null : row.key)}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{row.name}</span>
                      {row.size > 0 && <span className="shrink-0 font-normal text-muted-foreground">({(row.size / 1024).toFixed(1)} KB)</span>}
                    </button>
                    <div className="ml-2 flex shrink-0 items-center gap-2">
                      {status === "queued" && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          Queued{queuePosition ? ` (${queuePosition} of ${queue.queueSize})` : ""}
                        </span>
                      )}
                      {status === "processing" && (
                        <span className="flex items-center gap-1 text-xs text-blue-600">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing…
                        </span>
                      )}
                      {status === "done" && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle2 className="h-3.5 w-3.5" /> AI extracted
                        </span>
                      )}
                      {status === "failed" && (
                        <>
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="h-3.5 w-3.5" /> Extraction failed
                          </span>
                          <button
                            type="button"
                            title="Retry AI metadata extraction"
                            onClick={() => void retryExtraction(row)}
                            disabled={submitStage !== "idle"}
                            className="flex items-center gap-1 rounded border border-brand-teal px-2 py-1 text-xs text-brand-teal transition-colors hover:bg-brand-teal/10 disabled:opacity-50"
                          >
                            <RefreshCw className="h-3 w-3" /> Re-extract
                          </button>
                        </>
                      )}
                      {!filesUploaded && (
                        <button type="button" title="Remove file" onClick={() => removeRow(row.key)} className="rounded-full p-1 hover:bg-muted">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mounted for every uploaded row so values survive collapsing; hidden unless expanded. */}
                  {filesUploaded && (
                    <div className={isExpanded ? "border-t px-4 pb-4 pt-4" : "hidden"}>
                      <RowForm
                        row={row}
                        initial={blankValues(customFields, defaultOwnership)}
                        customFields={customFields}
                        ownershipValue={defaultOwnership}
                        locked={Boolean(lockedOwnership)}
                        register={registerForm}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3">
            {!filesUploaded ? (
              <Button
                type="button"
                className="gap-1.5 bg-brand-teal text-white hover:bg-brand-teal/90"
                disabled={rows.length === 0 || upload.isUploading}
                onClick={() => void handleUpload()}
              >
                <Upload className="h-4 w-4" /> Upload
              </Button>
            ) : (
              <Button
                type="submit"
                className="gap-1.5 bg-brand-teal text-white hover:bg-brand-teal/90"
                disabled={!settled || submitStage !== "idle"}
              >
                {isExtracting || submitStage !== "idle" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {submitLabel}
              </Button>
            )}
          </div>
        </form>
      )}

      <Dialog open={duplicates.length > 0} onOpenChange={(open) => !open && void resolveDuplicates(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Duplicate Files Detected
            </DialogTitle>
            <DialogDescription>
              The following file(s) appear to already exist in this project. Check any you&apos;d still like to process as a new entry anyway.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {duplicates.map(({ row, result }) => (
              <div key={row.key} className="flex items-start gap-3 rounded-md border p-3">
                <Checkbox
                  checked={duplicateChosen[row.key] ?? false}
                  onCheckedChange={(checked) => setDuplicateChosen((prev) => ({ ...prev, [row.key]: checked === true }))}
                  className="mt-1"
                />
                <div className="flex flex-col text-sm">
                  <span className="break-words font-medium text-foreground">{row.name}</span>
                  <span className="mt-0.5 text-xs text-muted-foreground">
                    Matches existing document: {result.matched_title || result.matched_filename}
                  </span>
                  <span className="mt-0.5 text-xs text-destructive">Same file already exists</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => void resolveDuplicates(false)}>
              Cancel All
            </Button>
            <Button type="button" className="gap-1.5 bg-brand-teal text-white hover:bg-brand-teal/90" onClick={() => void resolveDuplicates(true)}>
              {chosenCount > 0 ? `Proceed with ${chosenCount} Selected` : "Continue without"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentUploadForm;
